import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Star, X } from 'lucide-react';

interface ShopifyReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReview: () => void;
  onDismiss: () => void;
  milestoneReached?: string;
}

const getMilestoneMessage = (milestone?: string): string => {
  switch (milestone) {
    case 'first_order':
      return "🎉 Congratulations on your first order!";
    case '10_orders':
      return "🎊 Amazing! You've reached 10 orders!";
    case '50_orders':
      return "🚀 Incredible! 50 orders and counting!";
    case '10_affiliates':
      return "👥 Great work! You've enrolled 10 affiliates!";
    case '25_affiliates':
      return "🌟 Fantastic! 25 affiliates are working with you!";
    case '$1000_revenue':
      return "💰 Wow! You've generated $1,000 in revenue!";
    default:
      return "🎉 You're doing great with ReveShare!";
  }
};

export function ShopifyReviewModal({
  isOpen,
  onClose,
  onReview,
  onDismiss,
  milestoneReached,
}: ShopifyReviewModalProps) {
  const handleReview = () => {
    onReview();
    // Open Shopify App Store review page
    // You'll need to replace this with your actual Shopify app review URL
    window.open('https://apps.shopify.com/reveshare/reviews', '_blank');
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-card p-6 shadow-xl transition-all">
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-muted-foreground hover:text-muted-foreground transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Content */}
                <div className="text-center">
                  {/* Stars decoration */}
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-8 w-8 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>

                  {/* Title */}
                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-bold text-foreground mb-3"
                  >
                    {getMilestoneMessage(milestoneReached)}
                  </Dialog.Title>

                  {/* Description */}
                  <p className="text-muted-foreground mb-6">
                    We're thrilled to be part of your journey! If you're enjoying Reveshare, 
                    would you mind taking a moment to leave us a review on the Shopify App Store? 
                    It helps us tremendously and takes less than a minute.
                  </p>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleReview}
                      className="w-full bg-gradient-to-r from-blue-600 to-brand-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-brand-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      ⭐ Leave a Review
                    </button>

                    <button
                      onClick={onClose}
                      className="w-full bg-muted text-muted-foreground font-medium py-3 px-6 rounded-lg hover:bg-accent transition-colors"
                    >
                      Maybe Later
                    </button>

                    <button
                      onClick={onDismiss}
                      className="text-sm text-muted-foreground hover:text-muted-foreground transition-colors"
                    >
                      Don't ask me again
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}


